import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ISelectedContract, LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { UserClaims } from 'src/app/model/okta/userClaims';
import { AccountService } from 'src/app/core/services/account.service';
import { ADPResourceService, CustomFormControl, ListItem } from 'digital-core';
import { FormGroup } from '@angular/forms';
import { ConfirmationDialogData } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog-data';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';

@Component({
  selector: 'pgh-communication-preferences',
  templateUrl: './communication-preferences.component.html',
  styleUrls: ['./communication-preferences.component.scss'],
})
export class CommunicationPreferencesComponent implements OnInit, OnDestroy {
  language = '';
  smsConsent = false;
  isSaving = false;

  user: UserClaims;
  contract: ISelectedContract;

  isDialogLoaded: boolean = false;
  initialLoad: boolean = false;

  private $destroy = new Subject<boolean>();

  languages: ListItem[] = [];

  public commPrefsForm = new FormGroup({
    Languages: new CustomFormControl(this.language),
    SmsConsent: new CustomFormControl(this.smsConsent),
  });

  constructor(
    private loggedInUserSettings: LoggedInUserSettings,
    private dialog: MatDialog,
    private accountService: AccountService,
    private snackbarService: SnackbarService,
    private resourceService: ADPResourceService
  ) {}

  ngOnInit() {
    this.getLanguages();
    combineLatest([this.loggedInUserSettings.$selectedContract, this.loggedInUserSettings.$userClaims])
      .pipe(
        filter(([a, b]) => a !== null && b !== null),
        takeUntil(this.$destroy)
      )
      .subscribe(([contract, user]) => {
        this.user = user;
        this.contract = contract;
        this.language = this.contract.members[0].language;
        this.smsConsent = this.contract.members[0].contactConsent === 'Y' ? true : false;
        this.createForm();
      });

    this.initialLoad = true;
  }

  createForm() {
    this.commPrefsForm = new FormGroup({
      Languages: new CustomFormControl(this.language),
      SmsConsent: new CustomFormControl(this.smsConsent),
    });
  }

  ngOnDestroy() {
    this.$destroy.next(true);
  }

  getLanguages() {
    this.resourceService.getListItemResource('Global_Language_Preferences').subscribe((data) => {
      this.languages = data;
    });
  }

  getResource(title: string) {
    let resourceValue = this.resourceService.getViewResourceObject(title);
    return resourceValue;
  }

  openConfirmationModal() {
    const dialogData: ConfirmationDialogData = {
      title: 'Account_Communication_Preferences_Modal_Title',
      message: 'Account_Communication_Preferences_Modal_Message',
      cancelButtonText: 'Global_GoButton_Back',
      confirmButtonText: 'Global_OptOut',
      contextData: { titleLeftAligned: true },
    };

    const confirmationDialog = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
      data: dialogData,
    });

    confirmationDialog.afterClosed().subscribe((resp) => {
      if (resp === 'true') {
        this.save();
      } else {
        this.resetPreferences();
      }
    });
  }

  onSaveClicked() {
    if (this.commPrefsForm.controls.SmsConsent.value === false) {
      this.openConfirmationModal();
    } else {
      this.save();
    }
  }

  resetPreferences() {
    this.commPrefsForm.controls.SmsConsent.setValue(this.smsConsent);
    this.commPrefsForm.controls.Languages.setValue(this.language);
    this.commPrefsForm.markAsPristine();
  }

  save() {
    const smsConsent = this.commPrefsForm.controls.SmsConsent.value;
    const language = this.commPrefsForm.controls.Languages.value;
    const member = this.contract.members[0];
    this.isSaving = true;
    member.language = language;
    member.contactConsent = smsConsent;
    this.accountService.putEditMemberDetails(member.contractNumber, member.customerNumber, member).subscribe({
      next: (data) => {
        this.isSaving = false;
        this.snackbarService.showSimpleMessage(this.resourceService.getViewLabel('Account_Change_CommPrefs_Success_Message'));
        this.loggedInUserSettings.clearContractFromCache();
        this.contract.members[0].contactConsent = smsConsent;
        this.contract.members[0].language = language;
        this.smsConsent = smsConsent;
        this.language = language;
        this.resetPreferences();
      },
      error: (err) => {
        this.isSaving = false;
        this.resetPreferences();
        throw err;
      },
    });
  }

  getViewLabel(item: string) {
    const label = this.resourceService.getViewLabel(item) ?? '';
    return this.resourceService.transformContentData(label);
  }
}
